import {Component, Input, OnChanges, OnInit, signal, SimpleChanges} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {FieldConfig, FieldOption} from '../../../models/form-config.model';

/** Stato del filtro di visualizzazione sopra un gruppo di checkbox. */
type CheckboxFilterMode = 'all' | 'selected' | 'unselected';

/**
 * CheckboxField — gestisce due casi distinti:
 *
 * 1. **Singolo checkbox** (nessuna opzione nella config):
 *    - Il FormControl contiene un booleano (true/false).
 *    - Usato per "Accetto i termini", "Ricevi newsletter", ecc.
 *
 * 2. **Gruppo di checkbox** (con opzioni nella config):
 *    - Il FormControl contiene un array dei valori selezionati.
 *    - Ogni checkbox contribuisce all'array tramite la logica interna.
 *    - Usato per selezioni multiple (es. "Lingue parlate").
 */
@Component({
  selector: 'app-checkbox-field',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './checkbox-field.component.html',
  styleUrl: './checkbox-field.component.scss',
})
export class CheckboxFieldComponent implements OnInit, OnChanges {
  @Input({required: true}) field!: FieldConfig;
  @Input({required: true}) control!: FormControl;

  normalizedOptions: FieldOption[] = [];
  isSingle: boolean = false;

  /** ID base per accessibilità. */
  baseId: string = '';

  /** Filtro di visualizzazione corrente (solo se `field.checkboxFilter` è true). */
  readonly filterMode = signal<CheckboxFilterMode>('all');

  /**
   * Valori selezionati all'apertura del form, cioè lo stato salvato: il form
   * è costruito con il prefill prima che questo campo nasca, quindi il primo
   * valore del control è quello arrivato dal backend.
   *
   * Il filtro "associate / non associate" lavora su questi e non sulle spunte
   * correnti: le etichette parlano di associazioni esistenti, e filtrando
   * sulle spunte una "Seleziona tutte" svuoterebbe "non associate", mentre
   * togliere una spunta farebbe sparire la riga sotto il cursore.
   */
  private valoriSalvati = new Set<unknown>();

  ngOnChanges(changes: SimpleChanges): void {
    // Un control nuovo è un form ricostruito (es. prefill arrivato dopo):
    // lo stato salvato di riferimento è il suo.
    if (changes['control']) {
      const valori = this.control?.value;
      this.valoriSalvati = new Set(Array.isArray(valori) ? valori : []);
    }
  }

  ngOnInit(): void {
    this.baseId = `chk-${this.field.key}-${Math.random().toString(36).slice(2, 7)}`;
    this.isSingle = !this.field.options?.length;
    if (!this.isSingle) {
      this.normalizedOptions = this.normalizeOptions();
    }
  }

  get showFilter(): boolean {
    return !this.isSingle && !!this.field.checkboxFilter;
  }

  get showSelectAll(): boolean {
    return !this.isSingle && !!this.field.checkboxSelectAll;
  }

  /** Elenco a più righe con altezza fissa e scroll (attivo con filtro e/o seleziona-tutte). */
  get isListMode(): boolean {
    return this.showFilter || this.showSelectAll;
  }

  get allSelected(): boolean {
    return this.normalizedOptions.length > 0
      && this.normalizedOptions.every(o => this.isChecked(o.value));
  }

  get someSelected(): boolean {
    return !this.allSelected && this.normalizedOptions.some(o => this.isChecked(o.value));
  }

  /** Spunta o despunta in blocco TUTTE le opzioni, a prescindere dal filtro attivo. */
  onSelectAllChange(checked: boolean): void {
    if (this.control.disabled) return;

    const updated = checked ? this.normalizedOptions.map(o => o.value) : [];
    this.control.setValue(updated);
    this.control.markAsTouched();
  }

  /**
   * Opzioni effettivamente mostrate, in base al filtro di visualizzazione
   * attivo e allo stato salvato (vedi {@link valoriSalvati}).
   */
  get visibleOptions(): FieldOption[] {
    if (!this.showFilter) return this.normalizedOptions;

    switch (this.filterMode()) {
      case 'selected':
        return this.normalizedOptions.filter(o => this.valoriSalvati.has(o.value));
      case 'unselected':
        return this.normalizedOptions.filter(o => !this.valoriSalvati.has(o.value));
      default:
        return this.normalizedOptions;
    }
  }

  /**
   * True se l'opzione risulta associata nello stato salvato: in grassetto
   * nell'elenco. Solo con il filtro attivo, che è dove il campo rappresenta
   * delle associazioni; un gruppo di checkbox generico resta com'è.
   */
  isSalvata(value: unknown): boolean {
    return this.showFilter && this.valoriSalvati.has(value);
  }

  /** True se almeno un'opzione è associata nello stato salvato: mostra la legenda del grassetto. */
  get hasValoriSalvati(): boolean {
    return this.showFilter && this.normalizedOptions.some(o => this.valoriSalvati.has(o.value));
  }

  setFilterMode(mode: CheckboxFilterMode): void {
    this.filterMode.set(mode);
  }

  /** Verifica se un'opzione è selezionata nell'array di valori del control. */
  isChecked(value: unknown): boolean {
    const current = (this.control.value as unknown[]) ?? [];
    return current.includes(value);
  }

  /**
   * Aggiunge o rimuove un valore dall'array del FormControl.
   * Viene chiamato al cambio di ogni singolo checkbox nel gruppo.
   */
  onMultiChange(value: unknown, checked: boolean): void {
    if (this.control.disabled) return;

    const current: unknown[] = Array.isArray(this.control.value)
      ? [...this.control.value]
      : [];

    const updated = checked
      ? [...current, value]
      : current.filter(v => v !== value);

    this.control.setValue(updated);
    this.control.markAsTouched();
  }

  optionId(index: number): string {
    return `${this.baseId}-${index}`;
  }

  private normalizeOptions(): FieldOption[] {
    if (!this.field.options?.length) return [];
    const first = this.field.options[0];
    if (typeof first === 'string' || typeof first === 'number') {
      return (this.field.options as (string | number)[]).map(v => ({
        label: String(v),
        value: v,
      }));
    }
    return this.field.options as FieldOption[];
  }
}
